import type React from "react";
import { Link, useLocation } from "react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { BadgeCheck, Briefcase, Calendar, DollarSign, ExternalLink, MapPin, Sparkles } from "lucide-react";
import { Linkedin } from "@/components/icons/linkedin";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FreelancerListItem } from "@/types/freelancer";
import { formatTimeAgo } from "@/utils/dateUtils";
import { FreelancerActionsMenu } from "../FreelancerActionsMenu";
import { KanbanStageSelector } from "../KanbanStageSelector";
import { FreelancerScoreBadge } from "./FreelancerScoreBadge";
import { FreelancerSignalsRow } from "./FreelancerSignalsRow";
import { FreelancerAreasSummary } from "./FreelancerAreasSummary";
import { SearchHighlight } from "./SearchHighlight";
import { FreelancerSearchReasons } from "./FreelancerSearchReasons";
import { reasonsHiddenFromCard, type FreelancerSearchMatch } from "./searchMatch";
import { buildAvailabilityChip, formatLocation, topAttributes, buildFichaUrl } from "./freelancerCardShared";
import { FreelancerAvatar } from "./FreelancerAvatar";
import { ExternalLinkButton } from "@/components/ui/external-link-button";
import type { FreelancerResumoRapido } from "@/types/freelancer-painel";
import { LinkedInIcone, WhatsAppIcone } from "@/components/freelancers/contato/FreelancerContatoIcons";
import { cn } from "@/lib/utils";

import { DisponibilidadeBadge, DisponibilidadeDataHint } from "@/components/freelancers/DisponibilidadeBadge";
import { AlocacaoChip } from "@/components/freelancers/AlocacaoChip";
import type { DisponibilidadeSummary } from "@/utils/disponibilidadeUtils";

interface FreelancerCardWideProps {
  freelancer: FreelancerListItem;
  vagaId?: string;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
  /** Clique simples abre o painel do candidato; cmd/ctrl-clique segue para a ficha. */
  onOpenDetails?: () => void;
  resumoRapido?: FreelancerResumoRapido | null;
  disponibilidade?: DisponibilidadeSummary | null;
  filteredAreas?: string[];
  /** Busca livre da lista: termo a marcar e por que o perfil apareceu. */
  searchMatch?: FreelancerSearchMatch;
}

/** Card largo (1 por linha) — mais espaço para mini-charts e contexto. */
export function FreelancerCardWide({
  freelancer,
  vagaId,
  isSelected = false,
  onToggleSelect,
  onOpenDetails,
  disponibilidade,
  filteredAreas,
  searchMatch,
  resumoRapido,
}: FreelancerCardWideProps) {
  const location = useLocation();
  // No modo server as tags vêm na própria linha da RPC.
  const rating: { tags?: string[] } | null =
    (freelancer as { tags?: string[] }).tags?.length
      ? { tags: (freelancer as { tags?: string[] }).tags }
      : null;

  const fichaUrl = buildFichaUrl(freelancer.id, vagaId, location.search);
  const openPanelOnPlainClick = (event: React.MouseEvent) => {
    if (!onOpenDetails || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    onOpenDetails();
  };

  const loc = formatLocation(freelancer);
  const availability = buildAvailabilityChip(freelancer);
  const attrs = topAttributes(freelancer.atributos_medias, 6);
  const fieldTerm = searchMatch?.fieldTerm ?? "";
  const hiddenReasons = reasonsHiddenFromCard(searchMatch?.reasons ?? [], fieldTerm, loc);
  const isInactive = freelancer.is_active === false;

  return (
    <Card
      data-freelancer-id={freelancer.id}
      className={cn(
        "transition-colors hover:border-primary",
        isInactive && "opacity-60 bg-gray-50 border-gray-300",
        isSelected && "border-primary ring-2 ring-primary/30",
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {onToggleSelect && (
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggleSelect(freelancer.id)}
              onClick={(e) => e.stopPropagation()}
              className="mt-1"
              aria-label={`Selecionar ${freelancer.nome}`}
            />
          )}
          <Link
            to={fichaUrl}
            onClick={openPanelOnPlainClick}
            className="shrink-0 size-12 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center hover:bg-primary/20 transition-colors"
          >
            <FreelancerAvatar
              nome={freelancer.nome}
              fotoUrl={freelancer.linkedin_foto_url}
              openToWork={freelancer.open_to_work}
              openToWorkObservadoEm={freelancer.open_to_work_observado_em}
            />
          </Link>

          <div className="flex-1 min-w-0">
            {/* Cabeçalho */}
            <div className="flex items-start justify-between gap-2">
              <Link
                to={fichaUrl}
                onClick={openPanelOnPlainClick}
                className="block text-left min-w-0"
                data-testid="abrir-candidato"
              >
                <div className="flex items-center gap-1.5">
                  <h3 className="font-semibold truncate">
                    <SearchHighlight text={freelancer.nome} term={searchMatch?.nameTerm} />
                  </h3>
                  <DisponibilidadeBadge summary={disponibilidade} level="chip" />
                  <DisponibilidadeDataHint summary={disponibilidade} />
                  <AlocacaoChip summary={disponibilidade} />
                  <BadgeCheck
                    className={cn(
                      "h-4 w-4",
                      freelancer.quality_seal
                        ? "text-blue-500 fill-blue-500"
                        : "text-gray-300",
                    )}
                  />
                  {isInactive && (
                    <Badge
                      variant="outline"
                      className="shrink-0 border-red-200 bg-red-50 px-1.5 py-0 text-[10px] text-red-600"
                    >
                      inativo
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                  {freelancer.areas && freelancer.areas.length > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Briefcase className="h-3.5 w-3.5" />
                      <FreelancerAreasSummary
                        areas={freelancer.areas}
                        filteredAreas={filteredAreas}
                        searchTerm={fieldTerm}
                        maxVisible={3}
                      />
                    </span>
                  )}
                  {loc && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" />
                      <SearchHighlight text={loc} term={fieldTerm} />
                    </span>
                  )}
                </div>
              </Link>
              <FreelancerActionsMenu
                freelancerId={freelancer.id}
                freelancerName={freelancer.nome}
                freelancerEmail={freelancer.email}
                isActive={!isInactive}
                copyData={{
                  nome: freelancer.nome,
                  pretensao_salarial: freelancer.pretensao_salarial,
                  areas: freelancer.areas,
                  cidade: freelancer.cidade,
                  city: freelancer.city,
                  state: freelancer.state,
                  portfolio_url: freelancer.portfolio_url,
                  linkedin_url: freelancer.linkedin_url,
                }}
              />
            </div>

            <FreelancerSearchReasons reasons={hiddenReasons} term={fieldTerm} typedTerm={searchMatch?.nameTerm ?? ""} maxSnippets={3} className="mt-2" />

            {/* Scores + sinais lado a lado */}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <FreelancerScoreBadge freelancer={freelancer} />
              {typeof freelancer.media_avaliacao_perfil === "number" && (
                <Badge
                  variant="outline"
                  className="bg-purple-50 text-purple-700 border-purple-200 text-xs px-1.5 py-0 h-5"
                >
                  Perfil {freelancer.media_avaliacao_perfil.toFixed(1)}
                </Badge>
              )}
              {typeof freelancer.media_avaliacao_projeto === "number" && (
                <Badge
                  variant="outline"
                  className="bg-blue-50 text-blue-700 border-blue-200 text-xs px-1.5 py-0 h-5"
                >
                  Projeto {freelancer.media_avaliacao_projeto.toFixed(1)}
                </Badge>
              )}
              <span className="text-muted-foreground text-xs">·</span>
              <FreelancerSignalsRow freelancer={freelancer} density="compact" />
            </div>

            {/* Mini bars dos atributos */}
            {attrs.length > 0 && (
              <div className="mt-3 grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5">
                {attrs.map((a) => (
                  <TooltipProvider key={a.slug}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-muted-foreground capitalize w-20 truncate">
                            {a.nome.toLowerCase()}
                          </span>
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-purple-500"
                              style={{ width: `${Math.min(100, (a.media / 10) * 100)}%` }}
                            />
                          </div>
                          <span className="font-mono font-semibold w-8 text-right">
                            {a.media.toFixed(1)}
                          </span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        {a.nome} · média {a.media.toFixed(1)} em {a.total} avaliação(ões)
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
              </div>
            )}

            {/* Rodapé: pretensão, tags, links, data */}
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              {freelancer.pretensao_salarial && (
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <DollarSign className="h-3.5 w-3.5" />
                  <span className="font-medium text-foreground">
                    <SearchHighlight text={freelancer.pretensao_salarial} term={fieldTerm} />
                  </span>
                </span>
              )}
              {availability && (
                <span className="text-muted-foreground">{availability}</span>
              )}
              {rating?.tags && rating.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {rating.tags.slice(0, 3).map((tag) => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className="text-[11px] px-1.5 py-0 h-5"
                    >
                      {tag}
                    </Badge>
                  ))}
                  {rating.tags.length > 3 && (
                    <Badge variant="outline" className="text-[11px] px-1.5 py-0 h-5">
                      +{rating.tags.length - 3}
                    </Badge>
                  )}
                </div>
              )}
              {freelancer.auto_tags && freelancer.auto_tags.length > 0 && (
                <div
                  className="flex flex-wrap items-center gap-1"
                  title="Tags automáticas (extraídas por IA)"
                >
                  <Sparkles className="h-3 w-3 text-muted-foreground" />
                  {freelancer.auto_tags.slice(0, 3).map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="text-[11px] px-1.5 py-0 h-5 font-normal text-muted-foreground"
                    >
                      {tag}
                    </Badge>
                  ))}
                  {freelancer.auto_tags.length > 3 && (
                    <Badge
                      variant="secondary"
                      className="text-[11px] px-1.5 py-0 h-5 font-normal text-muted-foreground"
                    >
                      +{freelancer.auto_tags.length - 3}
                    </Badge>
                  )}
                </div>
              )}
              {freelancer.linkedin_ultima_revisao && (
                <span
                  className="ml-auto inline-flex items-center gap-1 text-blue-600"
                  title={`LinkedIn revisado em ${new Date(freelancer.linkedin_ultima_revisao).toLocaleString("pt-BR")}`}
                >
                  <Linkedin className="h-3 w-3" />
                  Revisado {formatTimeAgo(freelancer.linkedin_ultima_revisao).text}
                </span>
              )}
              <span
                className={`${freelancer.linkedin_ultima_revisao ? "" : "ml-auto "}inline-flex items-center gap-1 text-muted-foreground`}
              >
                <Calendar className="h-3 w-3" />
                {freelancer.last_submitted_at
                  ? formatTimeAgo(freelancer.last_submitted_at).text
                  : "Sem cadastro"}
              </span>
              <div className="flex items-center gap-0.5">
                <WhatsAppIcone resumo={resumoRapido?.whatsapp} telefone={freelancer.telefone} />
                <LinkedInIcone freelancerId={freelancer.id} url={freelancer.linkedin_url} resumo={resumoRapido?.linkedin} iconClassName="h-3.5 w-3.5" />
                <ExternalLinkButton
                  kind="portfolio"
                  url={freelancer.portfolio_url}
                  iconClassName="h-3.5 w-3.5"
                />
                <Button asChild variant="ghost" size="icon" className="h-7 w-7" title="Ver ficha completa">
                  <Link to={fichaUrl} aria-label="Ver ficha completa">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              </div>
            </div>

            {vagaId && (
              <div className="mt-3 pt-3 border-t">
                <KanbanStageSelector
                  vagaId={vagaId}
                  freelancerId={freelancer.id}
                  freelancerNome={freelancer.nome}
                  freelancerEmail={freelancer.email}
                />
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
